import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Info,
  MoreVertical,
  Pause,
  Pencil,
  Play,
  Plus,
} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { cn } from '@/lib/utils/helpers'
import { CronEditModal } from '../components/CronEditModal'
import {
  countActiveCronJobsFilters,
  CronJobsFilters,
  DEFAULT_CRON_JOBS_FILTERS,
  type CronJobsFiltersState,
} from '../components/CronJobsFilters'
import {
  getCronJobs,
  saveCronJob,
  toggleCronStatus,
} from '../services/jobsService'
import type { CronJob, CronListParams, CronSubmitPayload, CronStatus } from '../types'
import {
  cronDescriptionPreview,
  cronFrequencyKind,
  cronListParamsEqual,
  formatCronFrequency,
  formatCronTime,
  normalizeCronListParams,
} from '../utils/cronFormatters'

function CronDescriptionCell({
  html,
  isLastRow,
}: {
  html: string
  isLastRow: boolean
}) {
  const full = html ?? ''
  const preview = cronDescriptionPreview(full)

  if (!preview) {
    return <td className="overflow-hidden px-4 py-3 text-slate-700">—</td>
  }

  return (
    <td className="overflow-hidden px-4 py-3 text-slate-700">
      <InfoPopover
        inline
        contentHtml={full}
        align="start"
        maxWidth={448}
        isLastRow={isLastRow}
        testId="cron-description-popover"
        contentClassName="whitespace-normal text-sm leading-relaxed [&_a]:text-[#1597ff] [&_a]:underline [&_img]:max-w-full [&_p]:my-1"
      >
        <button
          type="button"
          className="block w-full max-w-full cursor-default border-0 bg-transparent p-0 text-left font-inherit text-inherit text-slate-700"
          aria-label="Show full description"
        >
          <span className="block truncate">{preview}</span>
        </button>
      </InfoPopover>
    </td>
  )
}

const SESSION_KEY = 'cronlist_filter'
const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
const DEFAULT_PER_PAGE = 15

type CronSessionFilter = {
  q?: string
  cron_id?: string
  cron_name?: string
  cron_status?: CronStatus | ''
  frequency?: CronJobsFiltersState['frequency']
}

function loadSessionFilter(): CronSessionFilter {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as CronSessionFilter
  } catch {
    return {}
  }
}

function saveSessionFilter(filter: CronSessionFilter) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(filter))
  } catch {
    // Ignore
  }
}

function clearSessionFilter() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Ignore
  }
}

function includesText(haystack: string | null | undefined, needle: string): boolean {
  if (!needle.trim()) return true
  return (haystack ?? '').toLowerCase().includes(needle.trim().toLowerCase())
}

function persistFilters(q: string, nextFilters: CronJobsFiltersState) {
  saveSessionFilter({
    q,
    cron_id: nextFilters.cron_id,
    cron_name: nextFilters.cron_name,
    cron_status: nextFilters.cron_status,
    frequency: nextFilters.frequency,
  })
}

/** Format next_run_at in UTC so it matches Cron Time (banner notes UTC). */
function formatNextRunUtc(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) return '—'
  return new Date(parsed).toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function CronJobsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [jobs, setJobs] = useState<CronJob[]>([])
  const [params, setParams] = useState<CronListParams>({})
  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState<CronJobsFiltersState>({ ...DEFAULT_CRON_JOBS_FILTERS })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CronJob | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [toggleTarget, setToggleTarget] = useState<CronJob | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [actionMenu, setActionMenu] = useState<{
    job: CronJob
    top: number
    left: number
  } | null>(null)
  const mountedRef = useRef(false)
  const filtersRef = useRef(filters)
  const actionMenuRef = useRef<HTMLDivElement | null>(null)
  filtersRef.current = filters

  const syncStatusToUrl = useCallback((cronStatus: CronStatus | '') => {
    const next = new URLSearchParams(searchParams)
    if (cronStatus) {
      next.set('cron_status', cronStatus)
    } else {
      next.delete('cron_status')
    }
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const load = useCallback(async (next: CronListParams, nextFilters: CronJobsFiltersState) => {
    setLoading(true)
    persistFilters(next.q ?? '', nextFilters)
    try {
      setJobs(await getCronJobs(next))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    const saved = loadSessionFilter()
    const urlStatus = searchParams.get('cron_status')
    const initialQ = saved.q ?? ''
    const initialStatus: CronStatus | '' =
      urlStatus === 'ACTIVE' || urlStatus === 'INACTIVE'
        ? urlStatus
        : ((saved.cron_status as CronStatus | '') ?? '')
    const initialFrequency =
      saved.frequency === 'daily'
      || saved.frequency === 'weekly'
      || saved.frequency === 'monthly'
        ? saved.frequency
        : ''
    const initialFilters: CronJobsFiltersState = {
      cron_id: saved.cron_id ?? '',
      cron_name: saved.cron_name ?? '',
      cron_status: initialStatus,
      frequency: initialFrequency,
    }
    setSearchInput(initialQ)
    setFilters(initialFilters)
    const initialParams = normalizeCronListParams(initialQ, initialStatus)
    setParams(initialParams)
    void load(initialParams, initialFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once; URL/session read on entry
  }, [load])

  const mergeAndLoad = useCallback(
    (q: string, nextFilters: CronJobsFiltersState) => {
      const next = normalizeCronListParams(q, nextFilters.cron_status)
      setParams((prev) => {
        const paramsChanged = !cronListParamsEqual(prev, next)
        if (paramsChanged) {
          void load(next, nextFilters)
          return next
        }
        persistFilters(q, nextFilters)
        return prev
      })
      syncStatusToUrl(nextFilters.cron_status)
      setPage(1)
    },
    [load, syncStatusToUrl],
  )

  useEffect(() => {
    const timer = window.setTimeout(() => {
      mergeAndLoad(searchInput, filtersRef.current)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchInput, mergeAndLoad])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const targetEl = event.target as Element | null
      if (targetEl?.closest?.('[data-cron-action-trigger]')) return
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setActionMenu(null)
      }
    }
    const onScrollOrResize = () => setActionMenu(null)
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [])

  const applyFilters = (nextFilters: CronJobsFiltersState) => {
    setFilters(nextFilters)
    mergeAndLoad(searchInput, nextFilters)
  }

  const resetFilters = () => {
    clearSessionFilter()
    const cleared = { ...DEFAULT_CRON_JOBS_FILTERS }
    setFilters(cleared)
    setSearchInput('')
    mergeAndLoad('', cleared)
  }

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (job: CronJob) => {
    setEditTarget(job)
    setEditOpen(true)
    setActionMenu(null)
  }

  const openActionMenu = (job: CronJob, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect()
    const menuWidth = 160
    const left = Math.min(
      Math.max(8, rect.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    )
    setActionMenu((current) => (
      current?.job.cron_id === job.cron_id
        ? null
        : { job, top: rect.bottom + 4, left }
    ))
  }

  const handleSubmit = async (payload: CronSubmitPayload, cronId?: string | number) => {
    setSubmitting(true)
    try {
      await saveCronJob(payload, cronId)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params, filters)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmToggle = async () => {
    if (!toggleTarget) return
    try {
      await toggleCronStatus(toggleTarget)
      showSuccess('Job Status Updated Successfully')
      setToggleTarget(null)
      void load(params, filters)
    } catch (error) {
      showApiError(error)
    }
  }

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => (
      includesText(String(job.cron_id ?? ''), filters.cron_id)
      && includesText(job.cron_name, filters.cron_name)
      && (!filters.frequency || cronFrequencyKind(job) === filters.frequency)
    ))
  }, [filters.cron_id, filters.cron_name, filters.frequency, jobs])

  const lastPage = Math.max(1, Math.ceil(filteredJobs.length / perPage) || 1)
  const safePage = Math.min(page, lastPage)
  const pagedJobs = useMemo(() => {
    const start = (safePage - 1) * perPage
    return filteredJobs.slice(start, start + perPage)
  }, [filteredJobs, perPage, safePage])

  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  const changePerPage = (next: number) => {
    setPerPage(next)
    setPage(1)
  }

  const activeFilterCount = countActiveCronJobsFilters(filters)

  return (
    <PageShell title="CRON JOBS" contentClassName="px-3 py-4 sm:px-6 sm:py-6 space-y-4">
      <ListSearchToolbar
        testIdPrefix="cron-jobs"
        searchValue={searchInput}
        searchPlaceholder="Search cron jobs..."
        onSearchChange={setSearchInput}
        onOpenFilters={() => setFiltersOpen(true)}
        activeFilterCount={activeFilterCount}
        actions={
          <button
            type="button"
            onClick={openAdd}
            data-testid="cron-jobs-add-button"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#0d7acc] hover:shadow-xl md:flex-none"
          >
            <Plus className="h-5 w-5" aria-hidden />
            Add New Cron
          </button>
        }
      />

      <CronJobsFilters
        filters={filters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      <div
        className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-3 text-sm text-slate-800"
        role="note"
        data-testid="cron-jobs-utc-banner"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
        <p>
          All cron times and next run times are displayed in <span className="font-semibold">UTC</span>.
        </p>
      </div>

      <ListingDataTableCard data-testid="cron-jobs-results-card">
        <ListPaginationBar
          placement="header"
          currentPage={safePage}
          lastPage={lastPage}
          totalRecords={filteredJobs.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={changePerPage}
          perPageOptions={[...PER_PAGE_OPTIONS]}
          showPerPageSelect
          summaryLayout="angular"
          testIdPrefix="cron-jobs"
        />

        <ListingDataTableScroll>
          <table className={cn(LISTING_DATA_TABLE_CLASS, 'w-full min-w-[1080px] table-fixed')}>
            <colgroup>
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[13%]" />
              <col className="w-[11%]" />
              <col className="w-[10%]" />
              <col className="w-[26%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
            </colgroup>
            <thead className={LISTING_DATA_TABLE_HEAD_CLASS}>
              <tr>
                <th className="px-4 py-3 text-left">Cron ID</th>
                <th className="px-4 py-3 text-left">Cron Name</th>
                <th className="px-4 py-3 text-left">Cron Time</th>
                <th className="px-4 py-3 text-left">Next Run</th>
                <th className="px-4 py-3 text-left">Frequency</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
              {loading ? (
                <ListingTableLoadingRow colSpan={8} />
              ) : pagedJobs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No cron jobs.
                  </td>
                </tr>
              ) : (
                pagedJobs.map((job, rowIndex) => (
                  <tr key={job.cron_id}>
                    <td className="overflow-hidden break-all px-4 py-3 text-slate-700">
                      {job.cron_id}
                    </td>
                    <td className="overflow-hidden break-words px-4 py-3 text-slate-700">
                      {job.cron_name || '—'}
                    </td>
                    <td className="overflow-hidden whitespace-nowrap px-4 py-3 text-slate-700">
                      {formatCronTime(job)}
                    </td>
                    <td className="overflow-hidden whitespace-nowrap px-4 py-3 text-slate-700">
                      {formatNextRunUtc(job.next_run_at)}
                    </td>
                    <td className="overflow-hidden whitespace-nowrap px-4 py-3 text-slate-700">
                      {formatCronFrequency(job)}
                    </td>
                    <CronDescriptionCell
                      html={job.cron_description ?? ''}
                      isLastRow={rowIndex === pagedJobs.length - 1}
                    />
                    <td className="overflow-hidden whitespace-nowrap px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          job.cron_status === 'ACTIVE'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-100 text-slate-600',
                        )}
                      >
                        {job.cron_status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <div className="flex flex-nowrap items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(job)}
                          className="shrink-0 rounded p-1.5 text-[#1597ff] hover:bg-[#1597ff]/10"
                          aria-label="Edit"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-cron-action-trigger
                          onClick={(event) => openActionMenu(job, event.currentTarget)}
                          className="shrink-0 rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          aria-label="More actions"
                          aria-haspopup="menu"
                          aria-expanded={actionMenu?.job.cron_id === job.cron_id}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ListingDataTableScroll>

        <ListPaginationBar
          placement="footer"
          currentPage={safePage}
          lastPage={lastPage}
          totalRecords={filteredJobs.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={changePerPage}
          perPageOptions={[...PER_PAGE_OPTIONS]}
          showPerPageSelect
          summaryLayout="angular"
          testIdPrefix="cron-jobs"
        />
      </ListingDataTableCard>

      {actionMenu
        ? createPortal(
            <div
              ref={actionMenuRef}
              role="menu"
              data-testid="cron-jobs-action-menu"
              className="fixed z-[80] min-w-[160px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
              style={{ top: actionMenu.top, left: actionMenu.left }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setToggleTarget(actionMenu.job)
                  setActionMenu(null)
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {actionMenu.job.cron_status === 'ACTIVE' ? (
                  <>
                    <Pause className="h-4 w-4 text-slate-500" aria-hidden />
                    Set Inactive
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 text-slate-500" aria-hidden />
                    Set Active
                  </>
                )}
              </button>
            </div>,
            document.body,
          )
        : null}

      <CronEditModal
        isOpen={editOpen}
        cronJob={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={toggleTarget !== null}
        title="Cron Status"
        message={
          toggleTarget
            ? `Are you sure you want to update cron status to ${toggleTarget.cron_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'}?`
            : ''
        }
        primaryLabel="Confirm"
        onPrimary={confirmToggle}
        secondaryLabel="Cancel"
        onSecondary={() => setToggleTarget(null)}
      />
    </PageShell>
  )
}
